import { Alert, Descriptions, Modal, Tag, Tooltip, Typography } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import type { OperationResult } from '@/shared/api/operationResult'
import { formatDateTime, utcTooltip } from '@/shared/lib/format'
import { formatMoneyValue } from '@/shared/lib/money'
import { ExpandableHint } from '@/shared/ui/ExpandableHint'
import { actorLabel } from '@/features/identity/actorName'
import { useActorDirectory } from '@/features/identity/useActorDirectory'
import { PAYROLL_STATE_COLOR } from './leaveState'
import {
  approvePayrollRun,
  cancelPayrollRun,
  isSelfApproval,
  markPayrollPaid,
  periodLabel,
  type PayrollRun,
} from './payrollApi'

export type PayrollAction = 'APPROVE' | 'PAID' | 'CANCEL'

/**
 * Qaror natijasi — server javobi VA o'z-o'zini tasdiqlash bayrog'i.
 *
 * ⚠️ IKKINCHISI SERVER JAVOBIDA YO'Q. `POST /approve` faqat
 * `OperationResult.OK` qaytaradi; `selfApproved` esa AUDIT yozuviga
 * tushadi (`PayrollServiceImpl` — «HAR DOIM yoziladi, `false` bo'lganda
 * ham»). Ya'ni bu faktni panel o'zi hisoblab, o'zi ko'rsatishi kerak —
 * aks holda operator hech qanday farq sezmasdi.
 */
export interface PayrollDecisionResult {
  action: PayrollAction
  result: OperationResult
  selfApproved: boolean
}

interface Props {
  action: PayrollAction
  run: PayrollRun
  /** Joriy foydalanuvchining LOGINI — `generatedBy` bilan solishtiriladi */
  username: string | null
  onClose: () => void
  onDone: (result: PayrollDecisionResult) => void
}

/**
 * Vedomost bo'yicha qaror — tasdiqlash, to'landi, bekor qilish.
 *
 * <h2>⚠️ O'ZINI TASDIQLASH — IKKI XIL JAVOB, IKKALASI HAM KO'RSATILADI</h2>
 * Backend qoidasi moslashuvchan (`PayrollServiceImpl#requireSoleApprover`):
 * <ul>
 *   <li>tashkilotda `hr.payroll.approve` li faol odam <b>bittadan
 *       ko'p</b> bo'lsa — <b>409</b>, matnida NECHTA odam borligi ham
 *       yozilgan. U shu oynada, xatolar joyida ko'rinadi;</li>
 *   <li><b>yagona odam</b> bo'lsa — RUXSAT, lekin audit izida
 *       `selfApproved: true`. Bu <b>muvaffaqiyat emas,
 *       OGOHLANTIRISH</b>: bir odam vedomostni ham yig'di, ham
 *       tasdiqladi.</li>
 * </ul>
 *
 * ⚠️ Shuning uchun oyna ochilganda ham OLDINDAN aytadi (tugmani
 * bosishdan oldin), natija qaytganda esa chaqiruvchi sahifa SARIQ
 * banner ko'rsatadi. «Bajarildi» deb yashil ko'rsatish — yolg'on
 * bo'lardi: amal izsiz o'tmadi.
 *
 * ⚠️ Uchinchi holat: `identity` javob bermasa tekshiruv YOPIQ ishlaydi
 * va 409 qaytadi. Bu ODATIY oqimni to'smaydi — boshqa odam
 * tasdiqlaganda `identity` ga chaqiruv umuman bo'lmaydi. Xabar shu
 * oynada o'qiladi.
 *
 * <h2>⚠️ XATO OYNANING ICHIDA (`guards.mjs` 1-bo'limi)</h2>
 * `onError` YO'Q. Yopishda `decide.reset()`.
 */
export function PayrollDecisionModal({ action, run, username, onClose, onDone }: Props) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  // ⚠️ N4: `generated_by` — aktyor identifikatori, NOM emas.
  //    ⚠️ `isSelfApproval` esa XOM qiymat bilan ishlashda davom etadi:
  //       unga ko'rsatiladigan matn berilsa, taqqoslash nom formati
  //       o'zgargan kuni jimgina yolg'on bo'lardi.
  const actors = useActorDirectory()

  const self = isSelfApproval(run, username)

  const decide = useMutation({
    mutationFn: (): Promise<OperationResult> => {
      if (action === 'APPROVE') return approvePayrollRun(run.id)
      if (action === 'PAID') return markPayrollPaid(run.id)
      return cancelPayrollRun(run.id)
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.hr.all })
      onDone({ action, result, selfApproved: action === 'APPROVE' && self })
      onClose()
    },
  })

  const close = () => {
    decide.reset()
    onClose()
  }

  return (
    <Modal
      open
      title={t(`hr.payroll.action.${action}.title`)}
      onCancel={close}
      onOk={() => decide.mutate()}
      confirmLoading={decide.isPending}
      okText={t(`hr.payroll.action.${action}.submit`)}
      okButtonProps={{ danger: action === 'CANCEL' }}
      cancelText={action === 'CANCEL' ? t('common.back') : t('common.cancel')}
      destroyOnHidden
      width={640}
    >
      <Alert
        type={action === 'CANCEL' ? 'warning' : 'info'}
        showIcon
        style={{ marginBottom: 16 }}
        message={t(`hr.payroll.action.${action}.note`)}
      />

      {/*
        ⚠️ OGOHLANTIRISH TUGMADAN OLDIN. Server bu holatni rad ETMASLIGI
        ham mumkin (yagona tasdiqlovchi bo'lsangiz), ya'ni operator
        «nega ishlamadi» emas, «bu qanday yoziladi» degan savolga javob
        olishi kerak — bosishdan OLDIN.
      */}
      {action === 'APPROVE' && self ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('hr.payroll.selfApproveTitle')}
          description={<ExpandableHint text={t('hr.payroll.selfApproveText')} fontSize={13} />}
        />
      ) : null}

      {decide.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(decide.error, t)}
        />
      ) : null}

      <Descriptions size="small" column={1} bordered>
        <Descriptions.Item label={t('hr.payroll.orgNode')}>
          {run.orgNodeName ?? run.orgNodeCode ?? '—'}
        </Descriptions.Item>
        <Descriptions.Item label={t('hr.payroll.period')}>{periodLabel(run)}</Descriptions.Item>
        <Descriptions.Item label={t('hr.payroll.state')}>
          <Tag color={PAYROLL_STATE_COLOR[run.state]}>{t(`payrollState.${run.state}`)}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('hr.payroll.lineCount')}>{run.lineCount}</Descriptions.Item>
        <Descriptions.Item label={t('hr.payroll.earning')}>
          {formatMoneyValue(run.earning)}
        </Descriptions.Item>
        <Descriptions.Item label={t('hr.payroll.deduction')}>
          {formatMoneyValue(run.deduction)}
        </Descriptions.Item>
        <Descriptions.Item label={t('hr.payroll.net')}>
          <Typography.Text strong>{formatMoneyValue(run.net)}</Typography.Text>
        </Descriptions.Item>
        <Descriptions.Item label={t('hr.payroll.generatedBy')}>
          {actorLabel(run.generatedBy, actors, t('common.dash'))}
          {run.generatedAt === null ? null : (
            <Tooltip title={utcTooltip(run.generatedAt)}>
              <Typography.Text type="secondary"> · {formatDateTime(run.generatedAt)}</Typography.Text>
            </Tooltip>
          )}
        </Descriptions.Item>
      </Descriptions>
    </Modal>
  )
}
